import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useChildTokens } from './useChildTokens';

/**
 * Stjärnsamlingen — barnets ⭐-innehav i EN kompakt ruta.
 *
 * Tidigare var detta ett rutnät om sju kolumner som växte med varje intjänad stjärna:
 * `Math.max(slots, earned, 7, ...)` gav en ruta per stjärna, så ett barn med 200
 * stjärnor fick 203 emojier på skärmen. Det tog hela sidan och såg kladdigt ut, vilket
 * var precis klagomålet.
 *
 * Nu är ytan KONSTANT. Samlingen räknas i tior:
 *   - en rad om tio platser visar var man är i den pågående tian,
 *   - antalet FÄRDIGA tior står som ett eget märke bredvid.
 * Tio platser går alltid att räkna med fingret och raden blir aldrig längre, hur många
 * stjärnor barnet än samlar. Det är också därför tian valdes framför den gamla sjuan:
 * "hur många tior" är begripligt även för den som inte läser siffror än.
 *
 * Stjärnan är ritad (SVG) i stället för emoji ⭐. Emojin renderas av systemets
 * teckensnitt — olika form på iOS, Android och i webbläsaren — och gick inte att färga
 * efter temat. Den ritade stjärnan följer barntemats gula och blir samma figur överallt.
 */

const STARS_PER_ROW = 10;

/** En ritad stjärna. `filled` = intjänad, annars en tom plats att fylla. */
const Star = ({ filled, size = 22 }: { filled: boolean; size?: number }) => {
  const tokens = useChildTokens();
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden
      sx={{
        width: size,
        height: size,
        display: 'block',
        flexShrink: 0,
        fill: filled ? 'warning.main' : 'transparent',
        stroke: filled ? 'warning.main' : tokens.border,
        strokeWidth: filled ? 0 : 1.5,
        strokeLinejoin: 'round',
      }}
    >
      <path d="M12 2.6l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.45 6.19 20.5l1.11-6.47-4.7-4.58 6.5-.95z" />
    </Box>
  );
};

type StarsGridProps = {
  /** Antal intjänade stjärnor totalt. */
  earned: number;
};

const StarsGrid = ({ earned }: StarsGridProps) => {
  const { t } = useTranslation();
  const tokens = useChildTokens();

  const safeEarned = Math.max(0, Math.floor(earned));
  const bundles = Math.floor(safeEarned / STARS_PER_ROW);
  const inRow = safeEarned % STARS_PER_ROW;

  return (
    <Box
      role="img"
      aria-label={t('child.stars.collectionAria', { count: safeEarned }) as string}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        bgcolor: tokens.card,
        borderRadius: '16px',
        border: `1px solid ${tokens.border}`,
        boxShadow: tokens.shadow,
      }}
    >
      {/* Färdiga tior. Döljs helt vid noll: ett märke som säger "×0" är brus. */}
      {bundles > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: '999px',
            bgcolor: tokens.amberLight,
            flexShrink: 0,
          }}
        >
          <Star filled size={18} />
          <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', color: tokens.text }}>
            ×{bundles}
          </Typography>
        </Box>
      )}

      {/* Den pågående tian. Alltid tio platser — ytan ändras aldrig. */}
      <Box sx={{ display: 'flex', gap: 0.5, flex: 1, justifyContent: 'space-between' }}>
        {Array.from({ length: STARS_PER_ROW }).map((_, index) => (
          <Star key={index} filled={index < inRow} />
        ))}
      </Box>
    </Box>
  );
};

export default StarsGrid;
